import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Check, Clock, Trash2, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { NOTE_COLORS } from './StickyPopup'
import { isDueNow, fmtTime, todayISO } from './reminderTime'
import { getEntityLinks, iconFor, deepLinkFor } from './reminderPayload'
import { SnoozeMenu } from './SnoozeMenu'
import { TruncateTip } from './TruncateTip'
import type { Reminder } from './types'

interface Props {
  rem: Reminder
  tz: string
  onToggle: () => void
  onSnoozeTo: (dueDate: string, dueTime: string | null) => void
  onDelete: () => void
  /** 保存内联编辑后的文本(trim 后非空且有变化才会被调用)。 */
  onSaveText?: (text: string) => void
  /** 点实体深链 chip 后调用 —— 让宿主(铃铛 popover)关闭,免得盖住打开的 flyout。 */
  onNavigate?: () => void
}

export function ReminderRow({ rem, tz, onToggle, onSnoozeTo, onDelete, onSaveText, onNavigate }: Props) {
  const { t } = useTranslation('reminders')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [snoozeOpen, setSnoozeOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  /** 进入编辑时的原文快照:commit/unmount flush 都跟它比,不跟 live rem.text 比——
   *  否则跨 tab 更新到达后,本地未修改的 draft 会把别人的新文本覆盖回旧值。 */
  const baselineRef = useRef('')
  /** 最新编辑状态的 ref 镜像,供 unmount flush 读取(cleanup 闭包里的 state 是陈旧的)。 */
  const flushRef = useRef<{ editing: boolean; draft: string; onSaveText?: (text: string) => void }>({ editing: false, draft: '' })
  flushRef.current = { editing, draft, onSaveText }

  useEffect(() => {
    if (editing && inputRef.current) {
      const el = inputRef.current
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }
  }, [editing])

  // unmount flush:点击 popover 外部时 Radix 在 pointerdown 就卸载整棵树,input 不触发 blur,
  // 编辑会静默丢失——cleanup 里把仍在编辑的有效改动保存掉。
  useEffect(() => () => {
    const { editing: stillEditing, draft: lastDraft, onSaveText: save } = flushRef.current
    if (!stillEditing) return
    const trimmed = lastDraft.trim()
    if (trimmed && trimmed !== baselineRef.current) save?.(trimmed)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only:cleanup 通过 flushRef 读最新值,不能依赖 state 否则每次输入都触发 cleanup 造成误保存
  }, [])

  const startEdit = () => {
    baselineRef.current = rem.text
    setDraft(rem.text)
    setEditing(true)
  }
  const commit = () => {
    flushRef.current.editing = false // 同步标记,防止 blur 保存后同帧 unmount 时 cleanup 重复 flush
    const trimmed = draft.trim()
    setEditing(false)
    if (!trimmed) return                      // 空文本视为取消(后端会 400)
    if (trimmed === baselineRef.current) return  // 相对进入编辑时的快照未修改,不发请求
    onSaveText?.(trimmed)
  }
  const cancel = () => {
    flushRef.current.editing = false
    setEditing(false)
  }

  const c = NOTE_COLORS[rem.color] ?? NOTE_COLORS.yellow
  const done = rem.status === 'done'
  const overdue = !done && rem.dueDate < todayISO(tz)
  const dueNow = !done && !overdue && isDueNow(rem, tz)
  const links = getEntityLinks(rem)

  return (
    <div className="rem-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 16px' }}>
      <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: c.edge, opacity: done ? 0.35 : 0.9, minHeight: 26 }} />
      <button onClick={onToggle} title={done ? t('markNotDone') : t('markDone')} style={{
        width: 18, height: 18, borderRadius: 99, flexShrink: 0, cursor: 'pointer', display: 'grid', placeItems: 'center',
        border: done ? 'none' : '1.5px solid hsl(var(--border))', background: done ? 'hsl(var(--primary))' : 'hsl(var(--background))', color: 'hsl(var(--primary-foreground))',
      }}>{done && <Check size={11} />}</button>
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <input
            ref={inputRef}
            className="rem-edit-input"
            value={draft}
            maxLength={200}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return // IME 组词中:Enter 是确认候选词、Esc 是关候选窗,不能当保存/取消
              if (e.key === 'Enter') { e.preventDefault(); commit() }
              else if (e.key === 'Escape') { e.preventDefault(); cancel() }
            }}
            onBlur={commit}
            style={{
              flex: 1, width: '100%', boxSizing: 'border-box', fontSize: 13, lineHeight: 1.35,
              padding: '3px 8px', borderRadius: 7, border: '1.5px solid hsl(var(--primary))', outline: 'none',
              background: 'hsl(var(--background))', color: 'hsl(var(--foreground))',
            }}
          />
        ) : (
          <div onDoubleClick={startEdit} style={{ cursor: 'text' }}>
            <TruncateTip tip={rem.text} style={{
              fontSize: 13, lineHeight: 1.35, color: done ? 'hsl(var(--muted-foreground))' : 'hsl(var(--foreground))', fontWeight: 500,
              textDecoration: done ? 'line-through' : 'none',
            }}>{rem.text}</TruncateTip>
          </div>
        )}
        {links.length > 0 && (
          <div style={{ display: 'flex', gap: 5, marginTop: 3, flexWrap: 'wrap' }}>
            {links.map((lk) => {
              const Icon = iconFor(lk.type)
              return (
                <Link key={`${lk.type}-${lk.id}`} to={deepLinkFor(lk)} onClick={(e) => { e.stopPropagation(); onNavigate?.() }} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: 180, fontSize: 10.5, fontWeight: 600,
                  color: 'hsl(var(--primary-text))', textDecoration: 'none', background: 'hsl(var(--accent))', borderRadius: 99, padding: '1px 7px',
                }}>
                  <Icon className="h-3 w-3 shrink-0" />
                  <TruncateTip tip={lk.label}>{lk.label}</TruncateTip>
                </Link>
              )
            })}
          </div>
        )}
      </div>
      {editing ? (
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <button title={t('save')} onMouseDown={(e) => { e.preventDefault(); commit() }} style={{
            width: 26, height: 26, display: 'grid', placeItems: 'center', borderRadius: 7, cursor: 'pointer',
            border: '1px solid #16A34A', background: 'hsl(var(--background))', color: '#16A34A',
          }}><Check size={14} /></button>
          <button title={t('cancel')} onMouseDown={(e) => { e.preventDefault(); cancel() }} style={{
            width: 26, height: 26, display: 'grid', placeItems: 'center', borderRadius: 7, cursor: 'pointer',
            border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', color: 'hsl(var(--muted-foreground))',
          }}><X size={14} /></button>
        </div>
      ) : (
        <>
          {/* 药丸底是半透明状态色（亮暗通用），文字走 tone token —— 写死深色 hex 在暗底上只有 ~2:1 */}
          {overdue && <span className="rem-pill" style={{ background: 'rgba(239, 68, 68, .16)', color: 'hsl(var(--tone-red))' }}>{t('overdue')}</span>}
          {dueNow && <span className="rem-pill" style={{ background: 'rgba(245, 158, 11, .18)', color: 'hsl(var(--tone-amber))' }}>{t('dueNow')}</span>}
          {!overdue && !dueNow && rem.dueTime && !done && (
            <span style={{ fontSize: 11.5, color: 'hsl(var(--muted-foreground))', fontWeight: 600, flexShrink: 0 }}>{fmtTime(rem.dueTime)}</span>
          )}
          {/* snooze 菜单打开时 actions 必须常驻:菜单 portal 到 body 后鼠标离开行,
              hover 态消失会让整组按钮(含触发器)淡出,视觉上菜单像是"悬空"的 */}
          <div className="rem-actions" style={{ display: 'flex', gap: 2, flexShrink: 0, ...(snoozeOpen ? { opacity: 1 } : {}) }}>
            {!done && (
              <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
                <PopoverTrigger asChild>
                  <button className="rem-icon-btn" title={t('snooze')} aria-label={t('snooze')}><Clock size={14} /></button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-auto p-1">
                  <SnoozeMenu tz={tz} currentTime={rem.dueTime}
                    onPick={(d, tm) => { setSnoozeOpen(false); onSnoozeTo(d, tm) }} />
                </PopoverContent>
              </Popover>
            )}
            <button className="rem-icon-btn" title={t('delete')} aria-label={t('delete')} onClick={onDelete}><Trash2 size={14} /></button>
          </div>
        </>
      )}
    </div>
  )
}
